/** Hàng ô số của thẻ «Tình hình kinh doanh» — dùng cho nút đang xem và khung chi tiết một người. */
import { StatCard } from "@/components/StatCard";
import { useIsMobile } from "@/hooks/useIsMobile";
import { formatIntVN, formatVND, formatVNDCompact } from "@/lib/format";
import type { SalesTeamOverviewMetrics } from "../api";
import { averageOrder, deltaPercent, targetPercent } from "../overview";

function Delta({ cur, prev }: { cur: number; prev: number | null | undefined }) {
  const d = deltaPercent(cur, prev);
  if (d == null) return <span>Chưa có tháng trước để so</span>;
  const tone = d > 0 ? "text-[var(--color-success-text)]" : d < 0 ? "text-destructive" : "";
  return (
    <span className={tone}>
      {d > 0 ? "▲" : d < 0 ? "▼" : "="} {Math.abs(d)}% so tháng trước
    </span>
  );
}

/**
 * Số tiền trong ô: đủ số trên máy tính (không cho chữ "đ" rơi dòng), gọn kiểu "839.5 tr"
 * trên điện thoại — ô hai cột không chứa nổi mười mấy chữ số.
 */
function Money({ v }: { v: number }) {
  const mobile = useIsMobile();
  return <span className="whitespace-nowrap text-[22px] md:text-[20px] xl:text-[22px]">{mobile ? formatVNDCompact(v) : formatVND(v)}</span>;
}

export function MetricTiles({
  m,
  prev,
  isPerson,
  narrow = false,
}: {
  m: SalesTeamOverviewMetrics;
  prev: SalesTeamOverviewMetrics | null;
  isPerson: boolean;
  /** Trong khung chi tiết hẹp: luôn hai cột, khổ màn rộng cũng vậy. */
  narrow?: boolean;
}) {
  const pct = targetPercent(m);
  const avg = averageOrder(m);
  const prevAvg = prev ? averageOrder(prev) : null;
  return (
    <div className="space-y-2">
      <div className={narrow ? "grid grid-cols-2 gap-3" : "grid grid-cols-2 gap-3 xl:grid-cols-4"}>
        <StatCard label="Doanh thu thực tế" value={<Money v={m.realRevenue} />} sub={<Delta cur={m.realRevenue} prev={prev?.realRevenue} />} />
        {isPerson ? null : (
          <StatCard
            label="Đạt mục tiêu"
            value={pct == null ? "Chưa đặt" : `${pct}%`}
            sub={pct == null ? "Tháng này chưa đặt mục tiêu" : `Mục tiêu ${formatVND(m.target)}`}
          />
        )}
        <StatCard label="Tiền khách đã đưa" value={<Money v={m.collected} />} sub={<Delta cur={m.collected} prev={prev?.collected} />} />
        <StatCard label="Doanh thu dự kiến" value={<Money v={m.expected} />} sub={<Delta cur={m.expected} prev={prev?.expected} />} />
        <StatCard label="Số đơn" value={formatIntVN(m.orders)} sub={<Delta cur={m.orders} prev={prev?.orders} />} />
        <StatCard
          label="Giá trị trung bình một đơn"
          value={avg == null ? "—" : <Money v={avg} />}
          sub={avg == null ? "Chưa có đơn" : <Delta cur={avg} prev={prevAvg} />}
        />
        <StatCard label="Doanh thu bán thêm" value={<Money v={m.upsell} />} sub={<Delta cur={m.upsell} prev={prev?.upsell} />} />
        {m.overdueLeads == null ? null : (
          <StatCard label="Khách quá hẹn gọi lại" value={formatIntVN(m.overdueLeads)} sub="Tính tới hôm nay" />
        )}
      </div>
      <p className="text-xs text-muted">
        Doanh thu thực tế tính theo ghi công khi khách trả đủ (một đơn có thể chia cho nhiều người); doanh thu dự kiến tính
        theo người bán trên đơn và ngày chụp hoặc khai giảng trong tháng — hai số có thể lệch nhau.
      </p>
    </div>
  );
}
